import { Label } from "@arcadeai/design-system/components/ui/atoms/label";
import {
  RadioGroup,
  RadioGroupItem,
} from "@arcadeai/design-system/components/ui/atoms/radio-group";
import { Skeleton } from "@arcadeai/design-system/components/ui/atoms/skeleton";
import { cn } from "@arcadeai/design-system/lib/utils";
import { FormField, FormItem, FormMessage } from "@arcadeai/design-system/rhf";
import { Link } from "@tanstack/react-router";
import { Building2, KeyRound, ShieldAlert, Users } from "lucide-react";
import { Suspense } from "react";
import type { Control } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { UserSourceSelector } from "../../components/form/user-source-selector";
import {
  AUDIENCE_TO_AUTH_TYPE,
  AUTH_TYPE_TO_AUDIENCE,
  type AudienceId,
  clearUserSourceUnlessExternal,
} from "../../lib/audience-auth";
import type { McpServerCreateRequest } from "../../types";

/**
 * "Who is this for?" audience picker for the create page.
 *
 * Members + External read as picker cards; the API key option is a quiet
 * fallback beneath them — an API key isn't a person, so it isn't a peer to the
 * audience cards. Each card maps onto a real auth type:
 *
 *   Members of this Project → arcade
 *   External users          → user_source
 *   API key                 → arcade_header
 *
 * The consequences of a choice reveal beneath it: External shows the
 * UserSourceSelector, API key shows the header advisory.
 */

type AudienceOption = {
  id: AudienceId;
  title: string;
  hint: string;
  Icon: React.ComponentType<{ className?: string }>;
};

const CARD_OPTIONS: AudienceOption[] = [
  {
    id: "internal",
    title: "Members of this Project",
    hint: "Sign in with their Arcade account",
    Icon: Users,
  },
  {
    id: "external",
    title: "External users",
    hint: "Sign in with your identity provider",
    Icon: Building2,
  },
];

const AUDIENCE_DETAIL: Record<AudienceId, string> = {
  internal:
    "Users authenticate with their Arcade account via OAuth. Recommended for development, testing, and internal use.",
  external:
    "End users sign in with your identity provider instead of Arcade accounts. Pick which User Source verifies them.",
  api_key:
    "Use this for clients or frameworks where you control the request headers.",
};

function HeaderAdvisory() {
  return (
    <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/5 p-2.5 text-sm">
      <ShieldAlert className="mt-0.5 size-4 shrink-0 text-warning" />
      <div className="min-w-0 space-y-1.5">
        <p className="font-medium text-xs">
          User identity is controlled by the caller
        </p>
        <p className="text-muted-foreground text-xs leading-snug">
          Send these headers from a server or MCP client you trust:
        </p>
        <div className="space-y-1">
          <div className="flex items-baseline gap-2">
            <code className="shrink-0 rounded bg-muted px-1 py-0.5 font-medium text-[11px] text-foreground">
              Authorization
            </code>
            <span className="min-w-0 text-muted-foreground text-xs">
              Bearer{" "}
              <Link
                className="text-link hover:underline"
                from="/orgs/$organizationId/projects/$projectId/api-keys"
                rel="noopener"
                target="_blank"
                to="."
              >
                your API key
              </Link>
            </span>
          </div>
          <div className="flex items-baseline gap-2">
            <code className="shrink-0 rounded bg-muted px-1 py-0.5 font-medium text-[11px] text-foreground">
              Arcade-User-Id
            </code>
            <span className="min-w-0 text-muted-foreground text-xs">
              the end-user's ID or email
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

type AudienceAuthFieldProps = {
  control: Control<McpServerCreateRequest>;
};

export function AudienceAuthField({ control }: AudienceAuthFieldProps) {
  const { setValue } = useFormContext<McpServerCreateRequest>();

  return (
    <FormField
      control={control}
      name="auth_type"
      render={({ field }) => {
        const selectedAudience = AUTH_TYPE_TO_AUDIENCE[field.value ?? "arcade"];

        const selectAudience = (audience: AudienceId) => {
          const authType = AUDIENCE_TO_AUTH_TYPE[audience];
          clearUserSourceUnlessExternal(setValue, authType);
          field.onChange(authType);
        };

        return (
          <FormItem className="space-y-3">
            <RadioGroup
              aria-labelledby="create-audience-label"
              className="block space-y-5"
              // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
              onValueChange={(value) => selectAudience(value as AudienceId)}
              value={selectedAudience}
            >
              <div>
                <div className="grid grid-cols-1 items-stretch gap-2 sm:grid-cols-2">
                  {CARD_OPTIONS.map(({ id, title, hint, Icon }) => {
                    const selected = selectedAudience === id;
                    return (
                      <div
                        className={cn(
                          "flex flex-col overflow-hidden rounded-lg border transition-colors",
                          selected
                            ? "border-foreground bg-muted/20"
                            : "border-border hover:border-foreground/40"
                        )}
                        key={id}
                      >
                        <Label
                          className="flex flex-1 cursor-pointer items-start gap-3 p-4"
                          htmlFor={`audience-${id}`}
                        >
                          <Icon
                            aria-hidden
                            className="mt-0.5 size-5 shrink-0 text-foreground"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block font-semibold text-sm leading-tight">
                              {title}
                            </span>
                            <span className="mt-0.5 block font-normal text-muted-foreground text-xs leading-snug">
                              {hint}
                            </span>
                          </span>
                          <RadioGroupItem
                            className="mt-0.5 shrink-0"
                            id={`audience-${id}`}
                            value={id}
                          />
                        </Label>
                        <p className="border-border/60 border-t bg-muted/40 px-4 py-2.5 text-muted-foreground text-xs leading-snug">
                          {AUDIENCE_DETAIL[id]}
                        </p>
                      </div>
                    );
                  })}
                </div>

                {selectedAudience === "external" ? (
                  <div className="motion-safe:fade-in relative mt-2 space-y-1.5 rounded-lg border border-foreground/20 bg-muted/30 p-4 motion-safe:animate-in motion-safe:duration-200">
                    <div
                      aria-hidden
                      className="absolute -top-1.5 left-1/2 size-3 -translate-x-1/2 rotate-45 border-foreground/20 border-t border-l bg-muted/30 sm:left-[75%]"
                    />
                    <p className="font-medium text-foreground text-sm">
                      User Source
                    </p>
                    <Suspense fallback={<Skeleton className="h-10 w-full" />}>
                      <UserSourceSelector autoSelectFirst control={control} />
                    </Suspense>
                  </div>
                ) : null}
              </div>

              <div
                className={cn(
                  "overflow-hidden rounded-lg border border-dashed transition-colors",
                  selectedAudience === "api_key"
                    ? "border-foreground/50 bg-muted/20"
                    : "border-border/70 hover:border-foreground/40"
                )}
              >
                <Label
                  className="flex w-full cursor-pointer items-center gap-2.5 px-3 py-2.5"
                  htmlFor="audience-api_key"
                >
                  <KeyRound
                    aria-hidden
                    className={cn(
                      "size-4 shrink-0",
                      selectedAudience === "api_key"
                        ? "text-foreground"
                        : "text-muted-foreground"
                    )}
                  />
                  <span className="min-w-0 flex-1 font-normal text-muted-foreground text-xs leading-snug">
                    <span className="font-medium text-foreground">
                      Using an MCP client without OAuth?
                    </span>{" "}
                    Authenticate with API key headers instead.
                  </span>
                  <RadioGroupItem
                    className="shrink-0"
                    id="audience-api_key"
                    value="api_key"
                  />
                </Label>
                {selectedAudience === "api_key" ? (
                  <div className="motion-safe:fade-in border-border/30 border-t bg-muted/20 px-3 py-3 motion-safe:animate-in motion-safe:duration-200">
                    <HeaderAdvisory />
                  </div>
                ) : null}
              </div>
            </RadioGroup>

            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
